import React from "react"

import isEqual from "lodash/isEqual"
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil"

import type { Proposal } from "~/graphql/client"
import { diff } from "~/utils/diff"

import { proposalLocalState, proposalRemoteState } from "./proposal-state"

const alwaysKeepLocalFields: (keyof Proposal)[] = [
  `downPayment`,
  `systemType`,
  `homeowner`,
  `name`,
  `displayAddress`,
  `annualEnergyUse`,
  `monthlyEnergyUse`,
  `monthlyEnergyCost`,
  `monthlyEnergyManualFlags`,
  `userAdjustedUtilityRate`,
  `requestType`,
  `selectedLoanProduct`,
  `financeType`,
  `financeProductId`,
]

export const useProposalOptimisticUpdate = (
  localRef: React.MutableRefObject<Proposal>,
): void => {
  const getLocalProposal = useRecoilCallback(({ snapshot }) => () => {
    const value = snapshot.getLoadable(proposalLocalState).contents

    return value
  })
  const setLocal = useSetRecoilState(proposalLocalState)
  const remote = useRecoilValue(proposalRemoteState)

  const remoteRef = React.useRef(remote)

  React.useEffect(() => {
    if (isEqual(remote, remoteRef.current)) {
      return
    }
    const previousLocalProposal = getLocalProposal()
    setLocal(remote)
    remoteRef.current = remote

    if (previousLocalProposal.id !== ``) {
      const localDiff = diff(remoteRef.current, previousLocalProposal) ?? {}
      const fieldsToUpdate = Object.keys(localDiff).reduce((acc, field) => {
        const key = field as keyof Proposal
        if (alwaysKeepLocalFields.includes(key)) {
          acc[key] = localDiff[key]
        }

        return acc
      }, {} as Partial<Proposal>)
      setLocal((currentLocalProposal) => ({
        ...currentLocalProposal,
        ...fieldsToUpdate,
      }))

      return
    }
  }, [remote, setLocal, getLocalProposal, localRef])
}
